import * as Yup from 'yup';
import { ERRORS } from '../constants/base';
import { latitudeInRange, longitudeInRange } from '../utils/coordinates';

// Single source of truth for the manual-address field rules so EVERY editor validates a
// hand-entered address identically — the front-office forms (contact Work, household Home,
// visit report) AND the admin notification-approval forms, where a reviewer can also toggle
// manual and type coordinates. Spread `manualAddressFieldTests()` into any address
// `Yup.object().shape({ ... })` to attach these three tests.

// A value counts as "filled" when it is not empty/null/undefined. Coordinates may arrive as
// numbers (from Google) or strings (manual entry), so test generically.
export const isFilled = (v: unknown): boolean => v !== '' && v !== null && v !== undefined;

// A MANUAL address the user has fully CLEARED — no address text, no place_id, no coordinates —
// is treated as "no address": the editor drops it before submit and the backend deletes the
// now-absent row. So the manual required-field rules below must NOT fire for an empty address
// (that would block the save and force the row's trash icon instead); they apply only once the
// address carries real content the user intends to keep.
export const manualAddressHasContent = (a: any): boolean =>
    !!(a?.fullAddress || a?.street_name || a?.formatted_address || a?.place_id) ||
    isFilled(a?.latitude) || isFilled(a?.longitude);

export const manualFieldRequired = (parent: any): boolean =>
    !!parent?.is_manual && manualAddressHasContent(parent);

// The workspace Default Region seeds BOTH the hidden country AND state onto a manual address. A
// kept manual address with BOTH empty means seeding genuinely failed (region never saved / a
// stale store), so the address would be "problematic" (dropped from state-based route division).
// A single one present is fine (e.g. a country with no state list seeds only the country). Used as
// the Yup `country` block below and by submit handlers to show the "please refresh" message.
export const manualRegionSeedFailed = (a: any): boolean =>
    manualFieldRequired(a) && !isFilled(a?.country) && !isFilled(a?.state);

// Scan a household/visit form's addresses (home + every contact's addresses) for a manual address
// whose region seeding failed, so the submit handler can surface the exact message. Path-tolerant:
// works for `homeAddress` + `persons[].addresses` and for `new_persons`/`updated_persons` shapes.
export const formHasManualRegionSeedFailed = (values: any): boolean => {
    if (!values) return false;
    const groups = [values.persons, values.new_persons, values.updated_persons].filter(Array.isArray);
    const addresses = [
        values.homeAddress,
        values.new_address,
        ...groups.flatMap((g: any[]) => g.flatMap((p) => (Array.isArray(p?.addresses) ? p.addresses : []))),
    ];
    return addresses.some((a) => manualRegionSeedFailed(a));
};

// Live inline coordinate errors for an address value, independent of any Yup/formik state — so a
// hand-typed OUT-OF-RANGE coordinate lights up its field the instant it is entered, identically
// in every editor (contact Work, household Home, visit report, admin approval). Only reports the
// unambiguous "wrong value" case (a filled-but-out-of-range number); "missing" is left to the
// schema/guard (submit-gated) so the field isn't red before the user has typed anything.
export const manualCoordinateInlineErrors = (a: any): { latitude?: string; longitude?: string } => {
    if (!manualFieldRequired(a)) return {};
    return {
        latitude:
            isFilled(a?.latitude) && !latitudeInRange(a.latitude) ? ERRORS.latitude_invalid : undefined,
        longitude:
            isFilled(a?.longitude) && !longitudeInRange(a.longitude) ? ERRORS.longitude_invalid : undefined,
    };
};

// The Yup test fragment shared by every address schema. Returns plain field entries so callers
// can spread them alongside their own (type / place_id / person_id) rules.
export const manualAddressFieldTests = () => ({
    // A manual address the user is KEEPING must carry its own coordinates (there is no geocode to
    // supply them), and they must be IN RANGE — a typo/out-of-range/swapped pair is rejected here
    // instead of failing on the server with a generic error.
    latitude: Yup.mixed().test('lat-required-range', ERRORS.latitude_invalid, function (v) {
        if (!manualFieldRequired(this.parent)) return true;
        if (!isFilled(v)) return this.createError({ message: ERRORS.required });
        if (!latitudeInRange(v)) return this.createError({ message: ERRORS.latitude_invalid });
        return true;
    }),
    longitude: Yup.mixed().test('lng-required-range', ERRORS.longitude_invalid, function (v) {
        if (!manualFieldRequired(this.parent)) return true;
        if (!isFilled(v)) return this.createError({ message: ERRORS.required });
        if (!longitudeInRange(v)) return this.createError({ message: ERRORS.longitude_invalid });
        return true;
    }),
    // Region-seed backstop. Country/State are HIDDEN and seeded from the workspace Default Region
    // (never typed). Entry is already gated on a region existing, so this only catches a genuine
    // seeding failure — BOTH country and state empty on a kept manual address (region never saved /
    // stale store) — which would make the address "problematic" (dropped from route division). A
    // single one present passes (a country with no state list seeds only the country). When a
    // region is correctly set this never trips.
    country: Yup.mixed().test('manual-region-seeded', ERRORS.manual_region_seed_failed, function (v) {
        if (!manualFieldRequired(this.parent)) return true;
        return isFilled(v) || isFilled(this.parent?.state);
    }),
});
